import { Link, Stack, Typography } from '@mui/material';
import { css } from '@emotion/react';
import { colors } from '../../../theme/colors';
import { ICompanyNewsItem } from '../../../schemas/CompanyProfile.schema';
import { panelSurfaceCss } from './panelStyles';

const panelCss = css`
  padding: 12px 16px 16px;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const titleCss = css`
  font-size: 16px;
  font-weight: 400;
  line-height: 1.5;
  letter-spacing: 0.15px;
  color: ${colors.text.main};
`;

const newsCardCss = css`
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 8px;
  border: 2px solid white;
  border-radius: 4px;
  background: ${colors.neutral[5]};
  box-shadow:
    0px 24px 48px 0px rgba(75, 82, 93, 0.02),
    0px 4px 24px 0px rgba(16, 37, 62, 0.04),
    0px -4px 40px 0px rgba(255, 255, 255, 0.06);
`;

const newsCardHeaderCss = css`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const newsSourceCss = css`
  font-size: 12px;
  line-height: 1.66;
  color: ${colors.text.main};
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const newsDateCss = css`
  font-size: 12px;
  line-height: 1.66;
  color: ${colors.text.secondary};
  flex-shrink: 0;
`;

const newsTitleCss = css`
  font-size: 12px;
  line-height: 1.66;
  color: ${colors.primary[60]};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

interface RecentNewsPanelProps {
  readonly news: readonly ICompanyNewsItem[];
}

/**
 * Profile-page Recent News panel. Shares the news-card visual treatment
 * with the sourcing grid's detail-row `RecentNewsSection` (same
 * neutral-grey background + soft shadow), but wraps it in the standard
 * `panelSurfaceCss` chrome so it lives alongside the other profile
 * panels.
 *
 * Renders nothing when there are no entries — the page conditionally
 * mounts this panel only when news is available, keeping the empty state
 * out of the main flow.
 */
export function RecentNewsPanel({ news }: RecentNewsPanelProps) {
  return (
    <div css={[panelSurfaceCss, panelCss]}>
      <Typography css={titleCss}>Recent News</Typography>
      <Stack gap='4px'>
        {news.map((item, index) => (
          // eslint-disable-next-line @eslint-react/no-array-index-key
          <NewsCard key={`${item.url}|${index}`} item={item} />
        ))}
      </Stack>
    </div>
  );
}

interface NewsCardProps {
  readonly item: ICompanyNewsItem;
}

function NewsCard({ item }: NewsCardProps) {
  return (
    <div css={newsCardCss}>
      <div css={newsCardHeaderCss}>
        <Typography css={newsSourceCss}>{item.source}</Typography>
        <Typography css={newsDateCss}>{item.date}</Typography>
      </div>
      <Link href={item.url} target='_blank' rel='noopener noreferrer' css={newsTitleCss} underline='hover'>
        {item.title}
      </Link>
    </div>
  );
}
